import { AspectRatio } from "@chakra-ui/react/aspect-ratio";
import { Flex } from "@chakra-ui/react/flex";
import { Icon } from "@chakra-ui/react/icon";
import { Image } from "@chakra-ui/react/image";
import { Link } from "@chakra-ui/react/link";
import { Text } from "@chakra-ui/react/text";

import React, { useEffect, useMemo, useState } from "react";
import { LuDownload, LuFile, LuFileQuestion } from "react-icons/lu";
import { useAppSelector } from "redux/hooks";
import { pauseOtherAudio } from "util/media-playback";

interface FilePreviewProps {
  file?: File;
  url?: string;
  type?: string;
  styles?: any;
  containerStyles?: any;
  isInteractive?: boolean;
}

export enum SIMPLE_TYPE {
  IMAGE = "image",
  VIDEO = "video",
  AUDIO = "audio",
  PDF = "pdf",
  BROKEN = "broken",
  UNKNOWN = "unknown",
}

export const getSimpleType = (type?: string | null): SIMPLE_TYPE => {
  if (!type) {
    return SIMPLE_TYPE.BROKEN;
  }

  if (type.includes("image")) {
    return SIMPLE_TYPE.IMAGE;
  }

  if (type.includes("video")) {
    return SIMPLE_TYPE.VIDEO;
  }

  if (type.includes("audio")) {
    return SIMPLE_TYPE.AUDIO;
  }

  if (type.includes("pdf")) {
    return SIMPLE_TYPE.PDF;
  }

  return SIMPLE_TYPE.UNKNOWN;
};

export const SUPPORTED_MEDIA_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "video/mp4",
  "audio/mpeg",
  "audio/mp3",
  "audio/mp4",
  "audio/ogg",
  "audio/ogg; codecs=opus",
  "application/pdf",
];

const FilePreview: React.FC<FilePreviewProps> = ({
  file,
  url,
  type,
  styles,
  containerStyles,
  isInteractive = true,
}) => {
  const { colorScheme } = useAppSelector((state) => state.theme);

  const [actualType, setActualType] = useState<string | null>(
    file?.type || type || null
  );
  const [actualUrl, setActualUrl] = useState<string | null>(
    (file ? URL.createObjectURL(file) : url) || null
  );

  useEffect(() => {
    setActualType(file?.type || type || null);
    setActualUrl((file ? URL.createObjectURL(file) : url) || null);
  }, [file, url, type]);

  const preview = useMemo<React.ReactNode>(() => {
    switch (getSimpleType(actualType)) {
      case SIMPLE_TYPE.IMAGE:
        return (
          <Image
            src={url}
            alt="Image Preview"
            objectFit="scale-down"
            borderRadius="lg"
            maxH="100%"
            maxW="100%"
            {...styles}
          />
        );
      case SIMPLE_TYPE.VIDEO:
        return (
          <AspectRatio
            borderRadius="lg"
            boxSize="10rem"
            maxH="100%"
            maxW="100%"
            {...styles}
          >
            <video
              {...(isInteractive ? { controls: true } : {})}
              style={{
                borderRadius: "0.5rem",
              }}
            >
              <source src={url} type={type} />
            </video>
          </AspectRatio>
        );
      case SIMPLE_TYPE.AUDIO:
        return (
          <AspectRatio borderRadius="lg" maxH="100%" maxW="100%" {...styles}>
            <audio
              {...(isInteractive ? { controls: true } : {})}
              onPlay={pauseOtherAudio}
              style={{
                borderRadius: "0.5rem",
              }}
            >
              <source src={url} type={type} />
            </audio>
          </AspectRatio>
        );
      case SIMPLE_TYPE.PDF:
        return (
          <Flex
            maxH="100%"
            maxW="100%"
            borderWidth="2px"
            borderStyle="solid"
            borderColor="border.muted"
            borderRadius="xl"
            direction="column"
            alignItems="center"
            justifyContent="center"
            p={4}
            {...containerStyles}
          >
            <Icon as={LuFile} boxSize="10rem" fill="fg.muted" {...styles} />
            {isInteractive ? (
              <>
                <Link
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={true}
                  display="inline-flex"
                  gap={2}
                  alignItems="center"
                >
                  <Text>Download</Text>
                  <Icon
                    as={LuDownload}
                    color={{
                      _light: `${colorScheme}.500`,
                      _dark: `${colorScheme}.200`,
                    }}
                  />
                </Link>
              </>
            ) : null}
          </Flex>
        );
      case SIMPLE_TYPE.BROKEN:
        return (
          <Flex
            maxH="100%"
            maxW="100%"
            borderWidth="1px"
            borderStyle="solid"
            borderColor="border.muted"
            borderRadius="xl"
            direction="column"
            alignItems="center"
            justifyContent="center"
            p={4}
            {...containerStyles}
          >
            <Icon
              as={LuFileQuestion}
              boxSize="10rem"
              borderRadius="lg"
              color="fg.muted"
              {...styles}
            />
          </Flex>
        );
      default:
        return (
          <Flex
            maxH="100%"
            maxW="100%"
            borderWidth="1px"
            borderStyle="solid"
            borderColor="border.muted"
            borderRadius="xl"
            direction="column"
            alignItems="center"
            justifyContent="center"
            p={4}
            {...containerStyles}
          >
            <Icon as={LuFile} boxSize="10rem" color="fg.muted" {...styles} />
            {isInteractive ? (
              <>
                <Link
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={true}
                  display="inline-flex"
                  gap={2}
                  alignItems="center"
                >
                  <Text>Download</Text>
                  <Icon
                    as={LuDownload}
                    color={{
                      _light: `${colorScheme}.500`,
                      _dark: `${colorScheme}.200`,
                    }}
                  />
                </Link>
              </>
            ) : null}
          </Flex>
        );
    }
  }, [actualType, actualUrl, isInteractive, styles, containerStyles]);

  return preview;
};

export default FilePreview;
